import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './index.css';

const languageMapping = {
  'pl-PL': 'Polski',
  'en-GB': 'English (UK)',
  'fr-FR': 'Français',
  'de-DE': 'Deutsch (Germany)',
  'de-CH': 'Schweizerdeutsch',
  'fr-CH': 'Français (Suisse)',
};

const TopBar = ({ langCode, setLangCode, category, setCategory, onHeightChange }) => {
  const [categories, setCategories] = useState([]);
  const languages = Object.keys(languageMapping);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const toolbarRef = useRef(null); // Use ref on Toolbar for height

  useLayoutEffect(() => {
    if (toolbarRef.current && typeof onHeightChange === 'function') {
      const height = toolbarRef.current.offsetHeight;
      onHeightChange(height);
    }
  }, [onHeightChange]);

  useEffect(() => {
    if (!langCode) {
      const browserLang = navigator.language || navigator.userLanguage;
      const detectedLang = languages.includes(browserLang) ? browserLang : 'en-GB';
      setLangCode(detectedLang);
    }
  }, [langCode, setLangCode, languages]);

  useEffect(() => {
    if (!langCode) return;

    const fetchCategories = async () => {
      try {
        const response = await axios.get(`https://notedin.onrender.com/api/datastream/?lang_code=${langCode}`);
        const metadata = response.data?.data || [];
        const cats = [...new Set(metadata.map(item => item.product_main_category).filter(Boolean))];
        setCategories(cats);
        if (!category && cats.length > 0) setCategory(cats[0]);
      } catch (err) {
        console.error('TopBar: Failed to fetch categories', err);
      }
    };

    fetchCategories();
  }, [langCode, category, setCategory]);

  return (
    <AppBar position="fixed" sx={{ backgroundColor: '#121212', zIndex: 1300 }}>
      <Toolbar
        ref={toolbarRef}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: { xs: '8px 12px', sm: '8px 16px', md: '10px 24px' },
        }}
      >
        {/* Logo + Title */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <img
              src="/repln.jpg"
              alt="repln.com"
              style={{
                width: isMobile ? '28px' : isTablet ? '36px' : '42px',
                marginRight: isMobile ? '8px' : '12px',
              }}
            />
          </Link>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#fff',
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
            }}
          >
            repln
          </Typography>
        </Box>

        {/* Language & Category */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2, md: 3 },
            flexWrap: 'nowrap', // Prevents wrapping
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          {/* Language Selector */}
          <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: isMobile ? 120 : isTablet ? 130 : 150, // Adjust for mobile screens
              width: 'auto',
            }}
          >
            <InputLabel id="language-select-label" sx={{ color: '#fff', fontSize: '0.75rem' }}>
              Language
            </InputLabel>
            <Select
              labelId="language-select-label"
              value={langCode || ''}
              onChange={(e) => {
                const newLangCode = e.target.value;
                setLangCode(newLangCode);
                setCategory(''); // Reset category on language change
                setCategories([]); // Reset categories
              }}
              label="Language"
              sx={{
                color: '#fff',
                '& .MuiOutlinedInput-root': { backgroundColor: '#333' },
                fontSize: '0.75rem', // Smaller font for the selected value
              }}
            >
              {languages.map((lang) => (
                <MenuItem key={lang} value={lang} sx={{ fontSize: '0.75rem' }}>
                  {languageMapping[lang]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Category Selector */}
          <FormControl
            variant="outlined"
            size="small"
            disabled={!langCode}
            sx={{
              minWidth: isMobile ? 120 : isTablet ? 140 : 170, // Adjust for mobile screens
              width: 'auto',
            }}
          >
            <InputLabel id="category-select-label" sx={{ color: '#fff', fontSize: '0.75rem' }}>
              Category
            </InputLabel>
            <Select
              labelId="category-select-label"
              value={category || ''}
              onChange={(e) => setCategory(e.target.value)}
              label="Category"
              sx={{
                color: '#fff',
                '& .MuiOutlinedInput-root': { backgroundColor: '#333' },
                fontSize: '0.75rem', // Smaller font for the selected value
              }}
            >
              {categories.length === 0 ? (
                <MenuItem value="">Loading...</MenuItem>
              ) : (
                categories.map((cat) => (
                  <MenuItem key={cat} value={cat} sx={{ fontSize: '0.75rem' }}>
                    {cat}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
